import React, { useState, useEffect } from 'react';
import { CommonInput } from '@/components/ui/common-input';
import { CommonSelect } from '@/components/ui/common-select';
import { CommonMultiSelect } from '@/components/ui/common-multi-select';
import { CommonButton } from '@/components/ui/common-button';
import { useToast } from '@/components/ui/use-toast';
import userService from '@/services/userService';
import masterDataService from '@/services/masterDataService';
import Loader from '@/components/ui/loader';
import { useAuth } from '@/contexts/AuthContext';

const EditUserForm = ({ user, onUpdateUser, onCancel, isTransporter, isSubmitting }) => {
  const { toast } = useToast();
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone_number: '',
    role: '',
    is_active: true,
    state_id: '',
    district_id: '',
    state_ids: [],
    port_ids: []
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);


  const [masterData, setMasterData] = useState({
    states: [],
    districts: [],
    ports: []
  });
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);

  const getFilteredRoleOptions = () => {
    const currentUserRole = userProfile?.role?.toLowerCase();
    const allRoleOptions = [
      { value: 'IPL_SUPER_ADMIN', label: 'Super Admin' },
      { value: 'port_admin', label: 'Port Admin' },
      { value: 'FIELD_OFFICER', label: 'Field Officer' },
      { value: 'ACCOUNT_HEAD', label: 'Account Head' },
      { value: 'REGION_HEAD', label: 'Region Head' },
      { value: 'DRIVER', label: 'Driver' },
      { value: 'TRANSPORTER', label: 'Transporter' },
      { value: 'DISTRIBUTOR', label: 'Distributor' },
      { value: 'RETAILER', label: 'Retailer' },
      { value: 'ACCOUNT_OFFICER', label: 'Account Officer' }
    ];

    if (currentUserRole === 'ipl_super_admin' || currentUserRole === 'ipl_admin') {
      return allRoleOptions;
    } else if (currentUserRole === 'transporter') {
      return [{ value: 'DRIVER', label: 'Driver' }];
    } else if (currentUserRole === 'port_admin') {
      return [
        { value: 'TRANSPORTER', label: 'Transporter' },
        { value: 'DISTRIBUTOR', label: 'Distributor' },
        { value: 'DRIVER', label: 'Driver' }
      ];
    } else if (['region_head', 'field_officer', 'account_head'].includes(currentUserRole)) {
      return [
        { value: 'TRANSPORTER', label: 'Transporter' },
        { value: 'DISTRIBUTOR', label: 'Distributor' },
        { value: 'RETAILER', label: 'Retailer' }
      ];
    }
    return [];
  };

  const roleOptions = getFilteredRoleOptions();

  const statusOptions = [
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' }
  ];

  useEffect(() => {
    if (user && user.id) {
      fetchUserById(user.id);
    } else if (user) {
      populateForm(user);
    }
  }, [user]);


  useEffect(() => {
    const fetchStates = async () => {
      try {
        setLoadingStates(true);
        const statesData = await masterDataService.getStates(true);
        setMasterData(prev => ({ ...prev, states: statesData || [] }));
      } catch (error) {
        console.error('Failed to fetch states:', error);
      } finally {
        setLoadingStates(false);
      }
    };

    const fetchPorts = async () => {
      try {
        const portsResponse = await masterDataService.getPorts(0, 500);
        let portsData = [];
        if (Array.isArray(portsResponse)) {
          portsData = portsResponse;
        } else if (portsResponse && typeof portsResponse === 'object') {
          if (Array.isArray(portsResponse.data)) {
            portsData = portsResponse.data;
          } else if (Array.isArray(portsResponse.ports)) {
            portsData = portsResponse.ports;
          }
        }
        setMasterData(prev => ({ ...prev, ports: portsData }));
      } catch (error) {
        console.error('Failed to fetch ports:', error);
      }
    };

    fetchStates();
    fetchPorts();
  }, []);


  useEffect(() => {
    const fetchDistricts = async () => {
      if (formData.state_id) {
        try {
          setLoadingDistricts(true);
          const districtsData = await masterDataService.getDistrictsByState(formData.state_id, true);
          setMasterData(prev => ({ ...prev, districts: districtsData || [] }));

          if (formData.district_id && !districtsData?.some(d => d.id === formData.district_id)) {
            setFormData(prev => ({ ...prev, district_id: '' }));
          }
        } catch (error) {
          console.error('Failed to fetch districts:', error);
          setMasterData(prev => ({ ...prev, districts: [] }));
        } finally {
          setLoadingDistricts(false);
        }
      } else {
        setMasterData(prev => ({ ...prev, districts: [] }));


        if (formData.district_id) {
          setFormData(prev => ({ ...prev, district_id: '' }));
        }
      }
    };

    fetchDistricts();
  }, [formData.state_id]);

  const fetchUserById = async (userId) => {
    setLoading(true);
    try {
      const userData = await userService.getUserById(userId);
      populateForm(userData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load user details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const populateForm = (userData) => {
    setFormData({
      username: userData.name || userData.username || '',
      email: userData.email || '',
      phone_number: userData.phone_number || '',
      role: userData.role ? convertRoleToAPIFormat(userData.role) : '',
      is_active: userData.status === 'Active' || userData.is_active === true,
      state_id: userData.state_id || '',
      district_id: userData.district_id || '',
      state_ids: userData.managed_states ? userData.managed_states.map(s => s.id) : (userData.state_ids || []),
      port_ids: userData.managed_ports ? userData.managed_ports.map(p => p.id.toString()) : (userData.port_ids || [])
    });
  };

  const convertRoleToAPIFormat = (role) => {
    if (!role) return '';

    const roleMap = {
      'Super Admin': 'IPL_SUPER_ADMIN',
      'IPL Admin': 'IPL_ADMIN',
      'IPL_ADMIN': 'IPL_ADMIN',
      'Port Admin': 'port_admin',
      'port_admin': 'port_admin',
      'Field Officer': 'FIELD_OFFICER',
      'FIELD_OFFICER': 'FIELD_OFFICER',
      'Account Head': 'ACCOUNT_HEAD',
      'ACCOUNT_HEAD': 'ACCOUNT_HEAD',
      'Region Head': 'REGION_HEAD',
      'REGION_HEAD': 'REGION_HEAD',
      'Driver': 'DRIVER',
      'DRIVER': 'DRIVER',
      'Transporter': 'TRANSPORTER',
      'TRANSPORTER': 'TRANSPORTER',
      'Distributor': 'DISTRIBUTOR',
      'DISTRIBUTOR': 'DISTRIBUTOR',
      'Retailer': 'RETAILER',
      'RETAILER': 'RETAILER',
      'Account Officer': 'ACCOUNT_OFFICER',
      'ACCOUNT_OFFICER': 'ACCOUNT_OFFICER'
    };

    const clean = role.toLowerCase();
    if (['ipl_super_admin', 'ipl_admin', 'port_admin', 'field_officer', 'account_head', 'region_head', 'driver', 'transporter', 'distributor', 'retailer', 'account_officer'].includes(clean)) {
      for (const key in roleMap) {
        if (key.toLowerCase() === clean) return roleMap[key];
      }
    }

    return roleMap[role] || role;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    const roleLower = formData.role ? formData.role.toLowerCase() : '';
    const isDriver = roleLower === 'driver';
    const isDistributor = roleLower === 'distributor';
    const isRetailer = roleLower === 'retailer';
    const isPortAdmin = roleLower === 'port_admin';
    const isFieldStaff = ['region_head', 'field_officer', 'account_head'].includes(roleLower);

    if (!isDriver) {
      if (!isDistributor && !formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (!formData.phone_number?.trim()) {
      newErrors.phone_number = 'Phone number is required';
    } else if (formData.phone_number.length !== 10) {
      newErrors.phone_number = 'Phone number must be exactly 10 digits';
    } else if (!/^[0-9]{10}$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Phone number must contain exactly 10 digits';
    }

    if (isFieldStaff && (!formData.state_ids || formData.state_ids.length === 0)) {
      newErrors.state_ids = 'At least one state is required for Field Officer';
    }

    if (isPortAdmin && (!formData.port_ids || formData.port_ids.length === 0)) {
      newErrors.port_ids = 'At least one port is required for Port Admin';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleProfileChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const roleLower = formData.role.toLowerCase();
      const updatedUserData = {
        id: user.id,
        username: formData.username,
        phone_number: formData.phone_number,
        role: formData.role,
        is_active: formData.is_active
      };

      if (roleLower !== 'driver') {
        updatedUserData.email = formData.email;
      }

      if (roleLower === 'distributor' || roleLower === 'retailer') {
        updatedUserData.state_id = formData.state_id || null;
        updatedUserData.district_id = formData.district_id || null;
      }

      if (['region_head', 'field_officer', 'account_head'].includes(roleLower)) {
        updatedUserData.state_ids = Array.isArray(formData.state_ids)
          ? formData.state_ids
          : [];
      }

      if (roleLower === 'port_admin') {
        updatedUserData.port_ids = Array.isArray(formData.port_ids)
          ? formData.port_ids
          : [];
      }

      onUpdateUser(updatedUserData);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const roleLower = formData.role ? formData.role.toLowerCase() : '';
  const isDriverRole = roleLower === 'driver';
  const isDistributorRole = roleLower === 'distributor';
  const showStateDistrict = roleLower === 'distributor' || roleLower === 'retailer';
  const isPortAdminRole = roleLower === 'port_admin';


  const stateOptions = masterData.states.map(state => ({
    value: state.id,
    label: state.state_name || state.name || state.id
  }));

  const districtOptions = masterData.districts.map(district => ({
    value: district.id,
    label: district.district_name || district.name || district.id
  }));

  const portOptions = (masterData.ports || [])
    .filter(port => port && port.id)
    .map(port => ({
      value: port.id.toString(),
      label: `${port.port_name || port.id} (${port.port_code || 'No Code'})`
    }));

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader size="lg" message="Loading user details..." />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex gap-4">
        <div className="flex-1">
          <CommonInput
            label="Username"
            id="username"
            value={formData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            error={errors.username}
            required
            placeholder="Enter username"
          />
        </div>

        {!isDriverRole && (
          <div className="flex-1">
            <CommonInput
              label={`Email ${isDistributorRole ? '(Optional)' : ''}`}
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              error={errors.email}
              required={!isDistributorRole}
              placeholder="Enter email address"
            />
          </div>
        )}
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <CommonInput
            label="Phone Number"
            id="phone_number"
            type="tel"
            value={formData.phone_number}
            onChange={(e) => {
              const inputValue = e.target.value;

              const sanitizedValue = inputValue.replace(/\D/g, '').slice(0, 10);
              handleChange('phone_number', sanitizedValue);
            }}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                e.preventDefault();
              }
            }}
            error={errors.phone_number}
            required
            placeholder="Enter 10-digit phone number"
            maxLength={10}
          />
        </div>

        <div className="flex-1">
          <CommonSelect
            label="Role"
            id="role"
            value={formData.role}
            onValueChange={(value) => handleChange('role', value)}
            options={roleOptions}
            error={errors.role}
            required
            placeholder="Select user role"
          />
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <CommonSelect
            label="Status"
            id="is_active"
            value={formData.is_active}
            onValueChange={(value) => handleChange('is_active', value === 'true' || value === true)}
            options={statusOptions}
            placeholder="Select user status"
          />
        </div>
      </div>


      {showStateDistrict && (
        <div className="flex gap-4">
          <div className="flex-1">
            <CommonSelect
              label="State"
              id="state_id"
              value={formData.state_id}
              onValueChange={(value) => handleProfileChange('state_id', value)}
              options={stateOptions}
              placeholder="Select state"
              searchable
              loading={loadingStates}
            />
          </div>

          <div className="flex-1">
            <CommonSelect
              label="District"
              id="district_id"
              value={formData.district_id}
              onValueChange={(value) => handleProfileChange('district_id', value)}
              options={districtOptions}
              placeholder="Select district"
              searchable
              loading={loadingDistricts}
              disabled={!formData.state_id}
            />
          </div>
        </div>
      )}
      {['region_head', 'field_officer', 'account_head'].includes(roleLower) && (
        <div className="border rounded-lg p-4 my-2">
          <h3 className="text-md font-medium mb-3">State Assignment</h3>
          <CommonMultiSelect
            label="Assigned States"
            id="state_ids"
            value={formData.state_ids || []}
            onChange={(selectedStateIds) => setFormData(prev => ({ ...prev, state_ids: selectedStateIds }))}
            options={stateOptions}
            error={errors.state_ids}
            required
            placeholder="Select State(s)"
            searchable
            loading={loadingStates}
          />
        </div>
      )}

      {isPortAdminRole && (
        <div className="border rounded-lg p-4 my-2">
          <h3 className="text-md font-medium mb-3">Port Assignment</h3>
          <CommonMultiSelect
            label="Assigned Ports"
            id="port_ids"
            value={formData.port_ids || []}
            onChange={(selectedPortIds) => setFormData(prev => ({ ...prev, port_ids: selectedPortIds }))}
            options={portOptions}
            error={errors.port_ids}
            required
            placeholder="Select Port(s)"
            searchable
          />
        </div>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        <CommonButton
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </CommonButton>
        <CommonButton
          type="submit"
          variant="default"
          loading={isSubmitting}
        >
          {isTransporter ? 'Update Driver' : 'Update User'}
        </CommonButton>
      </div>
    </form>
  );
};

export default EditUserForm;